import { ACTION_LABEL } from "@/lib/i18nLabels";
import { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import api, { formatDetail } from "@/lib/api";
import { formatIDR } from "@/lib/constants";
import { todayJKT } from "@/lib/tz";
import DatePicker from "@/components/DatePicker";

/**
 * Close-project confirmation dialog. Financial totals come from the ledger.
 * User spec 2026-02-10: close date wajib diisi (default: hari ini). Sisa Durasi
 * setelah close dihitung: end_date − close_date (bukan hari berjalan).
 */
export default function CloseProjectDialog({ project, open, onOpenChange, onDone }) {
  const [busy, setBusy] = useState(false);
  const existingEnd = project?.end_date || project?.vo_end_date || "";
  const [endDate, setEndDate] = useState(existingEnd ? existingEnd.slice(0, 10) : "");
  const [closeDate, setCloseDate] = useState(todayJKT());
  if (!project) return null;

  const inc = (project.income_entries || []).reduce((s, e) => s + (e.amount || 0), 0);
  const exp = (project.expense_entries || []).filter((e) => e.excluded_from_totals !== true).reduce((s, e) => s + (e.amount || 0), 0);
  const net = inc - exp;

  // Preview Sisa Durasi = end_date − close_date
  let durasiDelta = null;
  if (endDate && closeDate) {
    const eDt = new Date(endDate + "T00:00:00");
    const cDt = new Date(closeDate + "T00:00:00");
    if (!isNaN(eDt.getTime()) && !isNaN(cDt.getTime())) {
      durasiDelta = Math.round((eDt.getTime() - cDt.getTime()) / 86400000);
    }
  }

  const submit = async () => {
    if (!endDate) { toast.error("End Date wajib diisi"); return; }
    if (!closeDate) { toast.error("Tanggal Close Project wajib diisi"); return; }
    setBusy(true);
    try {
      await api.patch(`/active-projects/${project.id}/close`, {
        end_date: new Date(endDate).toISOString(),
        close_date: new Date(closeDate).toISOString(),
      });
      toast.success("Project closed and marked as completed.");
      onOpenChange(false);
      onDone?.();
    } catch (err) { toast.error(formatDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-lg" data-testid="dialog-close-project">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">Tutup Proyek</DialogTitle>
          <DialogDescription>
            Menutup <span className="font-semibold text-[#0B1D3A]">{project.project_name}</span>. Total buku kas saat ini akan dikunci dan proyek akan ditandai <span className="font-semibold">Selesai</span>.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-3 gap-3 border border-slate-200 p-4">
          <div><div className="overline text-slate-500">Kontrak</div><div className="font-display text-base tabular-nums mt-1">{formatIDR(project.contract_value ?? 0)}</div></div>
          <div><div className="overline text-slate-500">Total Pemasukan</div><div className="font-display text-base tabular-nums mt-1 text-emerald-700">{formatIDR(inc)}</div></div>
          <div><div className="overline text-slate-500">Total Pengeluaran</div><div className="font-display text-base tabular-nums mt-1 text-rose-700">{formatIDR(exp)}</div></div>
          <div className="col-span-3 border-t border-slate-200 pt-3">
            <div className="overline text-slate-500">Net</div>
            <div className={`font-display text-2xl tabular-nums mt-1 ${net >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{formatIDR(net)}</div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="overline text-slate-600">Tanggal Akhir Kontrak <span className="text-rose-600 normal-case">*</span></Label>
            <DatePicker
              testid="close-end-date"
              value={endDate}
              onChange={setEndDate}
              placeholder="End date kontrak…"
            />
            <p className="text-[9px] text-slate-500">Sesuai kontrak. {existingEnd ? "Tersedia dari data project." : ""}</p>
          </div>
          <div className="space-y-1.5">
            <Label className="overline text-slate-600">Tanggal Close Project <span className="text-rose-600 normal-case">*</span></Label>
            <DatePicker
              testid="close-close-date"
              value={closeDate}
              onChange={setCloseDate}
              placeholder="Tanggal proyek selesai…"
            />
            <p className="text-[9px] text-slate-500">Tanggal actual proyek selesai (default: hari ini).</p>
          </div>
        </div>
        {durasiDelta !== null && (
          <div className="border border-slate-200 bg-slate-50 px-3 py-2 flex items-center justify-between" data-testid="close-durasi-preview">
            <div className="overline text-slate-600 text-[10px]">Sisa Durasi vs Kontrak</div>
            <div className={`text-sm font-semibold tabular-nums ${
              durasiDelta > 0 ? "text-emerald-700" :
              durasiDelta === 0 ? "text-slate-700" :
              "text-rose-700"
            }`}>
              {durasiDelta > 0 ? `+${durasiDelta} hari lebih cepat` :
               durasiDelta === 0 ? "Tepat waktu" :
               `${Math.abs(durasiDelta)} hari lewat`}
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={() => onOpenChange(false)}>{ACTION_LABEL.cancel}</Button>
          <Button data-testid="cp-submit" onClick={submit} disabled={busy || !endDate || !closeDate} className="rounded-none bg-[#0B1D3A] hover:bg-[#1a2e52] disabled:opacity-50">
            {busy ? "Saving…" : "Close Project"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
